import { LinkProps } from "@mui/material";
import {
  FooterProps,
  RacwaBreadcrumbDetails,
  RacwaResponsiveHeaderProps,
  RacwaStepperTemplateProps,
} from "@racwa/react-components";
import { EMPTY_URL, InsurancePhoneNumber, PhoneButton } from "raci-react-library";
import { HeaderButtons, TestBanner } from "raci-react-library-npe";
import { EnvironmentHealthStatus } from "../../../__NPE__/EnvironmentHealthStatus";
import { PAGE_TITLE } from "../../constants";
import getB2CPCMUrl from "../../utils/getB2CPCMUrl";
import usePolicyNumber from "../usePolicyNumber";

type CommonPageProps = Pick<
  RacwaStepperTemplateProps,
  | "breadcrumbs"
  | "responsiveHeaderProps"
  | "headerEndAction"
  | "notificationBannerContent"
  | "showNotificationBanner"
  | "footerProps"
>;

const getLinkProps = (link?: string): Partial<LinkProps> => ({
  href: link ?? EMPTY_URL,
});

export const getBreadcrumbs = (policyNumber: string) => {
  return {
    links: [
      { name: "myRAC", href: import.meta.env.VITE_RAC_HOME_PAGE || EMPTY_URL, key: "breadcrumbMyRac" },
      {
        name: `Insurance ${policyNumber}`,
        href: getB2CPCMUrl(policyNumber),
        key: "breadcrumbPolicyNumber",
      },
    ],
    currentPage: { name: PAGE_TITLE },
  };
};

export const usePage = (): CommonPageProps => {
  const policyNumber = usePolicyNumber();
  const breadcrumbs: RacwaBreadcrumbDetails = getBreadcrumbs(policyNumber);
  const responsiveHeaderProps: RacwaResponsiveHeaderProps = {
    NavBreadcrumbsProps: {
      homeLink: import.meta.env.VITE_RAC_HOME_PAGE ?? EMPTY_URL,
    },
  };

  const footerProps: FooterProps = {
    variant: "sidebar",
    privacyLinkProps: getLinkProps(import.meta.env.VITE_RAC_ABOUT_PRIVACY_PAGE),
    securityLinkProps: getLinkProps(import.meta.env.VITE_RAC_ABOUT_SECURITY_PAGE),
    disclaimerLinkProps: getLinkProps(import.meta.env.VITE_RAC_ABOUT_DISCLAIMER_PAGE),
    accessibilityLinkProps: getLinkProps(import.meta.env.VITE_RAC_ABOUT_ACCESSIBILITY_PAGE),
  };

  let notificationBannerContent;

  const phoneButton = <PhoneButton phone={InsurancePhoneNumber} id={"header-phone-button"} />;
  let headerEndAction = phoneButton;

  /*
   ** Keep environment checks as compile-time constants.
   ** Vite removes NPE-only UI from PRD bundles.
   */
  if (
    import.meta.env.VITE_ENVIRONMENT === "LOCAL" ||
    import.meta.env.VITE_ENVIRONMENT === "DEV" ||
    import.meta.env.VITE_ENVIRONMENT === "SIT" ||
    import.meta.env.VITE_ENVIRONMENT === "UAT"
  ) {
    notificationBannerContent = <TestBanner />;

    let npeButtons;

    // Environment Health Status checks fail when targeting local BFF
    if (!(import.meta.env.VITE_API_BASE_URL ?? "").includes("localhost")) {
      npeButtons = [<EnvironmentHealthStatus />];
    }

    headerEndAction = (
      <HeaderButtons
        phoneButton={phoneButton}
        npeButtons={npeButtons}
        isUat={import.meta.env.VITE_ENVIRONMENT === "UAT"}
      />
    );
  }

  return {
    breadcrumbs,
    responsiveHeaderProps,
    headerEndAction,
    notificationBannerContent,
    showNotificationBanner: !!notificationBannerContent,
    footerProps,
  };
};

export default usePage;
